import { useEffect, useMemo, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KPI } from "@/components/Primitives";
import { UserPlus, Mail, KeyRound, Trash2, Copy, ShieldCheck, Search } from "lucide-react";
import { isClientRole } from "@/pages/SupportPortal";

const PRIVILEGED = ["super_admin", "msp_owner"];

function Secret({ label, value, onDone }) {
  return (
    <div className="bg-emerald-950/30 border border-emerald-600/30 rounded-md p-4 flex items-start gap-3" data-testid="secret-box">
      <ShieldCheck size={16} className="text-emerald-400 mt-0.5" />
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono mb-1">{label} — shown once</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-zinc-900/60 border border-zinc-800 rounded p-2 font-mono text-[12px] break-all" data-testid="secret-value">{value}</code>
          <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-zinc-300"><Copy size={14} /></button>
        </div>
      </div>
      <button onClick={onDone} className="text-zinc-500 hover:text-zinc-200 text-xs">Dismiss</button>
    </div>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("create"); // create | invite
  const [secret, setSecret] = useState(null); // {label, value}
  const [form, setForm] = useState({ email: "", name: "", role: "l1_technician", title: "", password: "", client_id: "" });
  const [inviteForm, setInviteForm] = useState({ email: "", role: "l1_technician", client_id: "" });
  const [companies, setCompanies] = useState([]);
  const [busy, setBusy] = useState(false);

  const iAmPrivileged = PRIVILEGED.includes(me?.role);
  const roleLabel = useMemo(() => Object.fromEntries(roles.map(r => [r.value, r.label])), [roles]);

  const load = () => api.get("/users").then(r => setUsers(asArray(r.data))).catch(() => setUsers([]));
  useEffect(() => {
    load();
    api.get("/roles").then(r => setRoles(asArray(r.data))).catch(() => setRoles([]));
    api.get("/clients").then(r => setCompanies(asArray(r.data))).catch(() => setCompanies([]));
  }, []);

  const assignable = roles.filter(r => iAmPrivileged || !r.privileged);

  const createUser = async () => {
    if (!form.email || !form.name) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/users", form);
      toast.success(`User ${data.name} created`);
      if (data.temp_password) setSecret({ label: `Temp password for ${data.email}`, value: data.temp_password });
      setForm({ email: "", name: "", role: "l1_technician", title: "", password: "", client_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const invite = async () => {
    if (!inviteForm.email) { toast.error("Email is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/invite", inviteForm);
      const link = `${window.location.origin}/register?invite=${encodeURIComponent(data.invite_token)}`;
      setSecret({ label: `Invitation link for ${inviteForm.email} (${data.expires_in_days}d)`, value: link });
      toast.success("Invitation created");
      setInviteForm({ email: "", role: "l1_technician", client_id: "" });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); toast.success(`${u.name} → ${roleLabel[role] || role}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: u.active === false }); toast.success(u.active === false ? "Activated" : "Deactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resetPw = async (u) => {
    try { const { data } = await api.post(`/users/${u.id}/reset-password`); setSecret({ label: `New temp password for ${u.email}`, value: data.temp_password }); toast.success("Password reset"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = users.filter(u =>
    !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()));
  const activeCount = users.filter(u => u.active !== false).length;
  const owners = users.filter(u => PRIVILEGED.includes(u.role)).length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Administration</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Users &amp; Access</h1>
          <p className="text-sm text-zinc-500 mt-1">Create operators, assign roles, invite by email, reset passwords, and deactivate access.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search users" data-testid="user-search" className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm w-64 focus:outline-none focus:border-blue-500/60" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total Users" value={users.length} testid="kpi-users-total" />
        <KPI label="Active" value={activeCount} tone="good" testid="kpi-users-active" />
        <KPI label="Deactivated" value={users.length - activeCount} tone={users.length - activeCount > 0 ? "warn" : "default"} testid="kpi-users-inactive" />
        <KPI label="Owners / Admins" value={owners} testid="kpi-users-owners" />
      </div>

      {secret && <Secret {...secret} onDone={() => setSecret(null)} />}

      {/* Add / Invite */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md">
        <div className="flex border-b border-zinc-900">
          <button onClick={() => setTab("create")} data-testid="tab-create" className={`px-5 py-3 text-sm font-medium inline-flex items-center gap-2 ${tab === "create" ? "text-zinc-100 border-b-2 border-blue-500" : "text-zinc-500"}`}><UserPlus size={14} /> Create user</button>
          <button onClick={() => setTab("invite")} data-testid="tab-invite" className={`px-5 py-3 text-sm font-medium inline-flex items-center gap-2 ${tab === "invite" ? "text-zinc-100 border-b-2 border-blue-500" : "text-zinc-500"}`}><Mail size={14} /> Invite by email</button>
        </div>

        {tab === "create" ? (
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="cu-name" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="cu-email" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="cu-role" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                {assignable.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Title (optional)" data-testid="cu-title" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
            </div>
            {isClientRole(form.role) && (
              <div className="mt-3">
                <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} data-testid="cu-company" className="w-full md:w-1/2 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                  <option value="">Company this end user belongs to…</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">End users see only their own requests, scoped to this company.</p>
              </div>
            )}
            <div className="flex items-center gap-3 mt-3">
              <input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Password (blank = auto-generate)" type="text" data-testid="cu-password" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm flex-1 font-mono" />
              <button onClick={createUser} disabled={busy} data-testid="cu-submit" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-4 py-2 whitespace-nowrap">Create user</button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">Leave the password blank to generate a one-time temp password (shown once). {iAmPrivileged ? "" : "Owner/Admin roles require an owner."}</p>
          </div>
        ) : (
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="Email to invite" data-testid="inv-email" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm md:col-span-1" />
              <select value={inviteForm.role} onChange={e => setInviteForm({ ...inviteForm, role: e.target.value })} data-testid="inv-role" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                {assignable.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <button onClick={invite} disabled={busy} data-testid="inv-submit" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-4 py-2">Generate invite link</button>
            </div>
            {isClientRole(inviteForm.role) && (
              <div className="mt-3">
                <select value={inviteForm.client_id} onChange={e => setInviteForm({ ...inviteForm, client_id: e.target.value })} data-testid="inv-company" className="w-full md:w-1/2 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                  <option value="">Company this end user belongs to…</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <p className="text-[11px] text-zinc-500 mt-2">Sends nothing automatically — copy the generated link and share it. The invitee sets their own password; role is preset.</p>
          </div>
        )}
      </div>

      {/* Users table */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium">Users ({filtered.length})</div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Role</th><th className="text-left px-4 py-2">Status</th><th className="text-right px-4 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.map(u => {
              const isMe = u.id === me?.id;
              const editingPrivileged = PRIVILEGED.includes(u.role);
              const canEditRole = iAmPrivileged || !editingPrivileged;
              return (
                <tr key={u.id} className="border-b border-zinc-900 hover:bg-zinc-900/30" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2">{u.name}{isMe && <span className="ml-2 text-[10px] text-blue-400 font-mono">you</span>}{u.title && <div className="text-[11px] text-zinc-500">{u.title}</div>}</td>
                  <td className="px-4 py-2 text-zinc-400 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2">
                    <select value={u.role} onChange={e => changeRole(u, e.target.value)} disabled={!canEditRole} data-testid={`user-role-${u.id}`}
                      className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs disabled:opacity-50">
                      {roles.map(r => <option key={r.value} value={r.value} disabled={r.privileged && !iAmPrivileged}>{r.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2">
                    <button onClick={() => toggleActive(u)} disabled={isMe} data-testid={`user-active-${u.id}`}
                      className={`text-[11px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${u.active === false ? "bg-zinc-900 border-zinc-800 text-zinc-500" : "bg-emerald-950/40 border-emerald-600/30 text-emerald-400"} disabled:opacity-50`}>
                      {u.active === false ? "disabled" : "active"}
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => resetPw(u)} title="Reset password" data-testid={`user-reset-${u.id}`} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-blue-400"><KeyRound size={14} /></button>
                      <button onClick={() => remove(u)} title="Delete user" disabled={isMe} data-testid={`user-delete-${u.id}`} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-500 hover:text-red-400 disabled:opacity-40"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-sm text-zinc-500">No users found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
